import React, { useEffect, useRef, useState } from "react";
import { ColumnValueRendererProps, StatusColumnValue } from "../table.data.interface";
import { MondayApiQueryInstance } from "../../../services/monday/monday-service-ts";
import logger from "../../../lib/logger";
import { Tooltip } from "@vibe/core";

interface LabelStyle {
  color: string;
}

interface StatusColumnData {
  label_style: LabelStyle;
  index: number;
  value: string;
  text: string;
  // Add other properties as needed based on the API response
}

const StatusColumn = ({
  columnData,
  itemIds,
  isPreview,
  otherBoardsValues,
}: ColumnValueRendererProps) => {
  const [statusColumnData, setStatusColumnData] = useState<StatusColumnData | null>(null);
  // Only the latest lookup may draw: an older one answering late would show the previous label.
  const requestRef = useRef(0);

  useEffect(() => {
    const request = ++requestRef.current;
    const fetchStatusColumnData = async (itemIds: number[]) => {
      try {
        const response = await MondayApiQueryInstance.getStatusColumnData(
          itemIds as number[],
          columnData.columnId,
        );

        if (response && request === requestRef.current) {
          setStatusColumnData(response.items[0].column_values[0]);
        }
      } catch (error: any) {
        logger.error("Error fetching status column data", error);
        throw error;
      }
    };

    if (itemIds && itemIds.length > 0 && !otherBoardsValues) {
      // Right after an edit the row already holds the new label and its colour: draw it now
      // rather than keep the old one up until monday answers.
      if (typeof columnData?.value === "string" && columnData.label_style?.color) {
        const text = columnData.value === "Empty Column" ? "" : columnData.value;
        setStatusColumnData((current) => ({
          index: current?.index ?? 0,
          value: text,
          text,
          label_style: columnData.label_style!,
        }));
      }
      fetchStatusColumnData(itemIds as number[]);
    } else if (otherBoardsValues) {
      const val = columnData.value;
      const valueIndex =
        val != null && typeof val === "object" && "index" in val
          ? (val as StatusColumnValue).index
          : undefined;
      setStatusColumnData({
        label_style: columnData.label_style ?? { color: "" },
        index: valueIndex ?? 0,
        value: columnData?.label ?? "",
        text: columnData?.label ?? "",
      });
    }
  }, [
    itemIds,
    columnData?.columnId,
    columnData?.value,
    columnData?.label_style,
    columnData?.text,
    columnData?.label,
    otherBoardsValues,
  ]);

  if (isPreview) {
    return (
      <Tooltip content={columnData?.text}>
        <div
          className={`flex overflow-hidden justify-center items-center px-3 py-1 font-semibold text-white whitespace-nowrap rounded min-w-[80px] h-[28px] text-ellipsis`}
          style={{
            backgroundColor: columnData?.label_style?.color || "",
          }}
        >
          <div className="truncate">{columnData?.text}</div>
        </div>
      </Tooltip>
    );
  }

  if (!statusColumnData && !isPreview) {
    return null; // or a placeholder
  }

  return (
    <div
      className={`px-3 py-1 font-semibold text-white rounded ${!statusColumnData?.text ? "min-w-[100px] h-[28px]" : ""}`}
      style={{
        backgroundColor: statusColumnData?.label_style?.color || "",
      }}
    >
      {statusColumnData?.text}
    </div>
  );
};

export default StatusColumn;
